//@ts-nocheck
import {createApp} from "vue/dist/vue.esm-bundler.js";
import {ref, computed, onMounted, reactive, watch, nextTick } from "vue";
import {AssessmentComponent} from "./assessment";
import {Loader} from "../../apps/components/Loader";
import {LoaderSize} from "../../enums/LoaderSize";
import {UnipageNavigatorAssessmentSource} from "../../enums/UnipageNavigatorAssessmentSource";
import {db_assessment, db_countries, db_education, db_programs, db_visas} from "./_data";
import {Navigator as NavigatorAPI} from "../../api/Navigator";
import {Request} from "../../api/Request";
import {clearFormInputsErrors, showFormInputsErrors} from "../../utils/_form-utils";
import {getCsrfToken} from "../../utils";
import {sanitizeHtml} from "../../utils/_sanitize-html";

window.UniPage = {
  DEFAULT_ANSWERS: {
    langs: [],
    langLevels: {},
    timeToStart: null,
    gpa: null,
    budget: null,
    regions: [],
    residencies: [],
    favorites: [],
    sortBy: 'default'
  },
  utils: {
    getLangName: (code) => {
      const langData = (window.LANGUAGES || {})[code];
      return (langData && typeof langData === 'object') ? langData.label : (langData || code);
    },
    getLevelLabel(score) {
      const s = score || 0;
      if (s < 1.0) return 'A0';
      if (s < 2.0) return 'A1';
      if (s < 3.0) return 'A2';
      if (s < 4.0) return 'B1';
      if (s < 5.0) return 'B2';
      if (s < 6.0) return 'C1';
      return 'C2';
    },
    formatMoney: (val) => new Intl.NumberFormat('ru-RU').format(val),
    formatMoneyRange(val) {
      const min = Math.round(val * 0.9);
      const max = Math.round(val * 1.1);
      return `${this.formatMoney(min)} – ${this.formatMoney(max)}`;
    },
    formatGpaRange(val) {
      const min = (val - 0.1).toFixed(1);
      const max = (val + 0.1).toFixed(1);
      return `${min} – ${max}`;
    },
    getVisaRiskStatus(risk) {
      if (risk >= 100) return { text: "Не выдают", color: "#dc3545" };
      if (risk >= 90)  return { text: "Критические", color: "#e45b31" };
      if (risk >= 40)  return { text: "Высокие", color: "#fd7e14" };
      if (risk >= 10)  return { text: "Значительные", color: "#ff9800" };
      if (risk >= 5)   return { text: "Умеренные", color: "#ffc107" };
      if (risk >= 2)   return { text: "Низкие", color: "#8bc34a" };
      return { text: "Минимальные", color: "#198754" };
    },
    formatValue: (val) => (val === null || val === undefined) ? null : String(val).replace('.', ','),
    formatRange(min, max) {
      if (min != null && max != null) {
        if (min === max) return this.formatValue(min);
        return `${this.formatValue(min)} – ${this.formatValue(max)}`;
      }
      if (min != null) return `${this.formatValue(min)}+`;
      if (max != null) return `${this.formatValue(max)}`;
      return null;
    },
    formatDeadlines(deadlines) {
      if (!Array.isArray(deadlines) || deadlines.length === 0) return null;
      const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
      const formatted = deadlines.map(d => d?.date).map(dateStr => {
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        if (parts.length === 2) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          return (month >= 1 && month <= 12) ? `${day} ${MONTHS_GEN[month - 1]}` : null;
        }
        if (parts.length === 1) {
          const month = parseInt(parts[0], 10);
          return (month >= 1 && month <= 12) ? `${MONTHS_NOM[month - 1]}` : null;
        }
        return null;
      }).filter(Boolean).slice(0, 2);
      return formatted.length > 0 ? formatted.join(' • ') : null;
    }
  }
};

// Data initialization
let DB_DATA = [];
let VISA_DATA = {};
let COUNTRY_DATA = {};
let COUNTRIES_BY_ISO = new Map();
let VISA_BY_ISO = new Map();

function loadAppData() {
  try {
    const dbData = JSON.parse(JSON.stringify(db_programs));
    const visaData = JSON.parse(JSON.stringify(db_visas));
    const countryData = JSON.parse(JSON.stringify(db_countries));
    const sharedData = JSON.parse(JSON.stringify(db_assessment));
    const educationData = JSON.parse(JSON.stringify(db_education));

    DB_DATA = dbData;
    VISA_DATA = visaData;
    COUNTRY_DATA = countryData;

    // Экспортируем общие данные в window для совместимости с кодом, который их ожидает
    Object.assign(window, sharedData);

    COUNTRIES_BY_ISO = new Map(Object.entries(countryData).map(([iso, data]) => [iso.toLowerCase(), data]));
    VISA_BY_ISO = new Map(Object.entries(visaData).map(([iso, data]) => [iso.toLowerCase(), data]));
    const EDUCATION_BY_ISO = new Map(Object.entries(educationData).map(([iso, data]) => [iso.toLowerCase(), data]));

    DB_DATA = DB_DATA.map(item => {
      const iso = (item.iso || "").toLowerCase();
      const countryInfo = COUNTRIES_BY_ISO.get(iso) || {};
      const visa = VISA_BY_ISO.get(iso) || null;
      const education = EDUCATION_BY_ISO.get(iso) || null;
      const costTotal = (item.costTuition || 0) + (countryInfo.costApartments || 0) + (countryInfo.costLife || 0);
      return { ...countryInfo, ...item, visa, education, costTotal };
    });

    // Экспортируем обработанную базу данных
    window.DB_DATA = DB_DATA;


  } catch (e) {
    console.error("Error loading data:", e);
  }
}

function initApp() {
  loadAppData()

  const app = createApp({
    components: {
      AssessmentComponent,
      Loader,
    },
    setup() {
      const STORAGE_KEY = (window.APP_CONFIG && window.APP_CONFIG.STORAGE_KEY) || 'unipage_assessment_data';
      const APP_CONFIG = window.APP_CONFIG || {};
      const answers = reactive({ ...window.UniPage.DEFAULT_ANSWERS });
      const resultsShown = ref(true);
      const showOnlyFavorites = ref(false);
      const showSortPanel = ref(false);
      const displayedCount = ref(10);
      const consent = ref<HTMLElement|null>(null);

      // Demo Mode Logic
      const isDemoMode = ref(false);
      const leadForm = reactive({ name: '', phone: '' });

      const isAssessmentModalOpen = ref(false);
      const assessmentInitialQuestionId = ref(null);
      const showBudgetWarning = ref(false);
      const isCriticalBudget = computed(() => answers.budget === 0);

      const updateDisplayMode = () => {
        if (isCriticalBudget.value) {
          isDemoMode.value = false;
          showBudgetWarning.value = true;
        } else {
          showBudgetWarning.value = false;
        }
      };

      watch(isCriticalBudget, updateDisplayMode, { immediate: true });

      const handleScholarshipsClick = () => {
        window.open(window.APP_CONFIG?.SCHOLARSHIPS_URL || 'https://www.unipage.net/ru/scholarships', '_blank');
      };
      const handleCancelBudget = () => {
        showBudgetWarning.value = false;
        openAssessmentModal('budget');
      };

      const sortBy = ref(answers.sortBy || 'default');
      const expandedIds = reactive(new Set());
      const expandedEducationIds = reactive(new Set());
      const toggleExpand = (res) => {
        const id = getFavoriteId(res);
        if (expandedIds.has(id)) {
          expandedIds.delete(id);
        } else {
          expandedIds.add(id);
        }
      };
      const isExpanded = (res) => expandedIds.has(getFavoriteId(res));

      const toggleEducation = (res) => {
        const id = getFavoriteId(res);
        if (expandedEducationIds.has(id)) {
          expandedEducationIds.delete(id);
        } else {
          expandedEducationIds.add(id);
        }
      };
      const isEducationExpanded = (res) => expandedEducationIds.has(getFavoriteId(res));

      const expandedUniversityIds = reactive(new Set());
      const toggleUniversity = async (res) => {
        const id = getFavoriteId(res);
        if (expandedUniversityIds.has(id)) {
          expandedUniversityIds.delete(id);
        } else {
          expandedUniversityIds.add(id);

          await fillUniversitiesListContainersIfEmpty(res.id);
        }
      };

      const getUniversitiesListHTML = async (countryId) => {
        try {
          const response = await fetch(`${document.location.origin}/ru/universities?country_ids[]=${countryId}`, {
            headers: {
              "X-Requested-With": "XMLHttpRequest"
            },
          });
          const data = await response.json();

          const responseContentWrapper = document.createElement("div");
          responseContentWrapper.innerHTML = data.content;

          return responseContentWrapper.querySelector(".grid-content")?.outerHTML;
        } catch (error) {
          console.error("Error fetching universities list:", error);
          return null;
        }
      }

      const fillUniversitiesListContainersIfEmpty = async (countryId) => {
        const universitiesListContainers = document.querySelectorAll(`.js-universities-list-container-${countryId}`);
        const universitiesListLoaders = document.querySelectorAll(`.js-universities-list-loader-${countryId}`);
        for (const container of universitiesListContainers) {
          if (!container?.hasChildNodes()) {
            const universitiesListHTML = await getUniversitiesListHTML(countryId);

            if (universitiesListHTML) {
              container.innerHTML = sanitizeHtml(universitiesListHTML);
            }

            for (const loader of universitiesListLoaders) {
              loader.remove();
            }
          }
        }
      }

      const isUniversityExpanded = (res) => expandedUniversityIds.has(getFavoriteId(res));

      const sortOptions = [
        { value: 'default', label: 'Самые подходящие - Все критерии' },
        { value: 'chances', label: 'Наивысшие шансы поступления' },
        { value: 'regions', label: 'Выбранные регионы' },
        { value: 'recommended', label: 'Лучшие направления от экспертов UniPage' },
        { value: 'cheapest', label: 'Самые доступные варианты' },
        { value: 'quality', label: 'Самое качественное образование' },
        { value: 'popularity', label: 'Самые популярные страны' }
      ];
      const openAssessmentModal = (initialId = null) => {
        assessmentInitialQuestionId.value = (initialId && typeof initialId === 'string') ? initialId : null;
        isAssessmentModalOpen.value = true;
      };
      const closeAssessmentModal = async () => {
        isAssessmentModalOpen.value = false;
        // Возвращаем предупреждение, если бюджет критический
        if (isCriticalBudget.value) {
          showBudgetWarning.value = true;
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            Object.assign(answers, data);
          } catch (e) {
            console.error("Error updating assessment data", e);
          }
        }

        await prepareAndSaveResults();
      };

      const onAssessmentComplete = async (newAnswers) => {
        isAssessmentModalOpen.value = false;
        if (newAnswers) {
          Object.assign(answers, newAnswers);
          updateDisplayMode();
          scrollToFirstResult();
          await prepareAndSaveResults();
        }
      };

      const scrollToFirstResult = () => {
        nextTick(() => {
          const resultsScreen = document.querySelector('.results-screen');
          if (resultsScreen) {
            // Прокручиваем к началу результатов
            resultsScreen.scrollIntoView({ behavior: 'smooth', block: 'start' });

            // Фокусируемся на первой карточке, если она есть
            const firstCard = resultsScreen.querySelector('.country-card');
            if (firstCard) {
              firstCard.setAttribute('tabindex', '-1');
              firstCard.focus({ preventScroll: true });
            }
          }
        });
      };

      onMounted(() => {
        window.addEventListener('storage', (e) => {
          if (e.key === STORAGE_KEY && e.newValue) {
            try {
              const newData = JSON.parse(e.newValue);
              Object.assign(answers, newData);
              if (newData.sortBy) sortBy.value = newData.sortBy;
            } catch (ex) { console.error(ex); }
          }
        });

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            Object.assign(answers, data);
            if (data.sortBy) sortBy.value = data.sortBy;
          } catch (e) {
            console.error("Error loading assessment data", e);
          }
        } else {
          // Если пользователь не отвечал на вопросы, открываем форму при запуске
          openAssessmentModal();
        }

        const savedShowFavorites = localStorage.getItem('unipage_show_only_favorites');
        if (savedShowFavorites !== null) {
          showOnlyFavorites.value = JSON.parse(savedShowFavorites);
        }

        const paragraph = document.querySelector('.js-navigator-consent');
        if (consent.value && paragraph) {
          consent.value.replaceChildren(paragraph);
          paragraph.classList.remove("hidden");
        }
      });

      const filteredResults = computed(() => calculateChances());

      async function prepareAndSaveResults() {
        const hasAssessment = !!localStorage.getItem(STORAGE_KEY);

        // 1. JSON с параметрами анкеты (исключая null, контакты и дефолты)
        const payload = {};
        for (const key in answers) {
          const val = answers[key];
          // Исключаем null и контактные данные
          if (val === null || key === 'contactName' || key === 'contactPhone') continue;

          // Исключаем данные по умолчанию, если анкета не заполнялась
          if (!hasAssessment && key in window.UniPage.DEFAULT_ANSWERS) {
            // Специальная логика для языков (если выбраны все - это дефолт)
            if (key === 'langs' && Array.isArray(val) && val.length >= window.UniPage.DEFAULT_ANSWERS.langs.length && window.UniPage.DEFAULT_ANSWERS.langs.length > 0) continue;
            if (key === 'langLevels' && typeof val === 'object' && Object.keys(val).length >= window.UniPage.DEFAULT_ANSWERS.langs.length && window.UniPage.DEFAULT_ANSWERS.langs.length > 0) continue;

            // Для остальных полей сравниваем значения
            if (JSON.stringify(val) === JSON.stringify(window.UniPage.DEFAULT_ANSWERS[key])) {
              if (key !== 'favorites') continue;
            }
          }

          payload[key] = val;
        }

        // 2. Человекопонятный текст для Менеджера
        let text = "РЕЗЮМЕ ЗАЯВКИ ДЛЯ МЕНЕДЖЕРА:\n";

        // Языки
        const langsText = answers.langs.map(l => {
          const name = window.UniPage.utils.getLangName(l);
          const levelVal = answers.langLevels?.[l];
          const levelObj = (window.LANG_LEVELS || []).find(lvl => lvl.value === levelVal);
          const levelLabel = levelObj ? levelObj.label : levelVal;
          return `${name} (${levelLabel})`;
        }).join(", ");
        text += `- Языки обучения: ${langsText || (window.UniPage.DEFAULT_ANSWERS.langs.length > 0 ? 'Любые' : 'Не указаны')}\n`;

        // Время начала
        const isDefaultTime = !hasAssessment && answers.timeToStart === window.UniPage.DEFAULT_ANSWERS.timeToStart;
        const timeOption = (window.TIME_TO_START_OPTIONS || []).find(o => o.value === answers.timeToStart);
        text += `- Срок подготовки: ${isDefaultTime ? 'Не указан' : (timeOption ? timeOption.label : answers.timeToStart + ' мес.')}\n`;

        // GPA
        const isDefaultGPA = !hasAssessment && answers.gpa === window.UniPage.DEFAULT_ANSWERS.gpa;
        const gpaOption = (window.GPA_OPTIONS || []).find(o => o.value === answers.gpa);
        text += `- Успеваемость (GPA): ${isDefaultGPA ? 'Не указана' : (answers.gpa + (gpaOption ? ' (' + gpaOption.label + ')' : ''))}\n`;

        // Бюджет
        const isDefaultBudget = !hasAssessment && answers.budget === window.UniPage.DEFAULT_ANSWERS.budget;
        const budgetOption = (window.BUDGET_OPTIONS || []).find(o => o.value === answers.budget);
        text += `- Бюджет в год (обучение+жизнь): ${isDefaultBudget ? 'Не указан' : (budgetOption ? budgetOption.label : answers.budget + ' $')}\n`;

        // Регионы
        const regionsText = (answers.regions || []).map(r => {
          const regionObj = (window.REGION_OPTIONS || []).find(o => o.value === r);
          return regionObj ? regionObj.label : r;
        }).join(", ");
        text += `- Желаемые регионы: ${regionsText || 'Любые'}\n`;

        // Гражданство
        const isDefaultResidency = !hasAssessment && JSON.stringify(answers.residencies) === JSON.stringify(window.UniPage.DEFAULT_ANSWERS.residencies);
        const residencyText = (isDefaultResidency ? [] : (answers.residencies || [])).map(r => {
          const residencyObj = (window.RESIDENCY_OPTIONS || []).find(o => o.value === r);
          return residencyObj ? residencyObj.label : r;
        }).join(", ");
        text += `- Гражданство/ВНЖ: ${residencyText || 'Не указано'}\n`;

        // Упорядочивание
        const currentSort = sortOptions.find(o => o.value === sortBy.value);
        text += `- Упорядочивание: ${currentSort ? currentSort.label : sortBy.value}\n`;


        // Избранное
        const favorites = answers.favorites || [];
        if (favorites.length > 0) {
          text += `\n--- ИЗБРАННЫЕ ПРОГРАММЫ ---\n`;
          favorites.forEach(favId => {
            const program = DB_DATA.find(p => getFavoriteId(p) === favId);
            if (program) {
              const countryInfo = COUNTRIES_BY_ISO.get(program.iso?.toLowerCase()) || {};
              const countryName = countryInfo.country || program.iso;
              const langData = window.LANGUAGES?.[program.langCode];
              const langName = (langData && typeof langData === 'object') ? langData.label : (langData || program.langCode);
              text += `- ${countryName} (${langName}, ${program.uniType})\n`;
            }
          });
        }

        await saveResults(payload, text);
      }

      const saveResults = async (results, textResults) => {
        await NavigatorAPI.results(results, textResults, getSourceFromQuery());
      }

      function calculateChances() {
        const results = DB_DATA.filter(item => item.status !== 0).map(item => {
          const userGPA = answers.gpa;
          const reqGPA = item.gpaLevel;
          let bonusGPA = 0, penaltyGPA = 0;
          if (userGPA >= reqGPA) {
            bonusGPA = reqGPA === 4.0 ? 0 : ((userGPA - reqGPA) / (4.0 - reqGPA)) * 25;
          } else {
            penaltyGPA = (Math.abs(userGPA - reqGPA) / reqGPA) * 30;
          }

          const isLangMatch = answers.langs.includes(item.langCode);
          const userLangLevel = (answers.langLevels[item.langCode] !== undefined) ? answers.langLevels[item.langCode] : 0;
          const timeBonusMap = { 0: 0, 3: 0.24, 6: 0.48, 12: 0.96, 24: 1.92, 48: 3.84 };
          const langGrowth = timeBonusMap[answers.timeToStart] || 0;
          const potentialLangLevel = Math.min(6.0, userLangLevel + langGrowth);

          const reqLangLevel = item.langScore;
          let bonusLang = 0, penaltyLang = 0;
          if (potentialLangLevel >= reqLangLevel) {
            bonusLang = reqLangLevel >= 5.0 ? 0 : ((potentialLangLevel - reqLangLevel) / (5.0 - reqLangLevel)) * 25;
          } else {
            penaltyLang = (Math.abs(potentialLangLevel - reqLangLevel) / reqLangLevel) * 30;
          }

          let penaltyBudget = 0;
          if (answers.budget < item.costTotal) {
            const missFraction = (item.costTotal - answers.budget) / item.costTotal;
            penaltyBudget = 100 * (Math.log(1 + missFraction) / Math.log(1.3));
          }

          let chance = 30 + bonusGPA + bonusLang - penaltyGPA - penaltyLang - penaltyBudget;
          chance = Math.max(0, Math.min(80, Math.round(chance)));

          let visaRisk = 0;
          let workingResidencyLabel = "";
          if (item.visa && item.visa.visa_risks) {
            let minRisk = Infinity;
            let bestResCode = null;

            for (const resCode of answers.residencies) {
              let riskEntry = item.visa.visa_risks.find(r => {
                const codes = r.residencies.split(',').map(c => c.trim().toLowerCase());
                return codes.includes(resCode.toLowerCase());
              });

              if (!riskEntry) {
                riskEntry = item.visa.visa_risks.find(r => {
                  const codes = r.residencies.split(',').map(c => c.trim().toLowerCase());
                  return codes.includes('other');
                });
              }

              if (riskEntry && riskEntry.risk < minRisk) {
                minRisk = riskEntry.risk;
                bestResCode = resCode;
              }
            }

            if (bestResCode) {
              visaRisk = minRisk;
              workingResidencyLabel = window.RESIDENCY_OPTIONS.find(o => o.value === bestResCode)?.label || bestResCode;
              chance = Math.max(0, Math.min(80, Math.round(chance * (100 - visaRisk) / 100)));
            }
          }

          const unipageChance = Math.round(100 * (1 - Math.pow(1 - chance / 100, 5)));
          let countryPriority = unipageChance + 0.3 * (40 - item.quality) * unipageChance / 100;

          const recommendedValue = item.recommended !== undefined ? item.recommended : 1;
          countryPriority *= recommendedValue;

          let isPreferred = false;
          if (answers.regions.length > 0 && answers.regions.includes(item.groupCode)) {
            isPreferred = true;
          }
          if (answers.regions.length > 0 && answers.regions.includes(item.groupCode) && chance > 0) {
            countryPriority *= 2;
          }

          return {
            ...item,
            selfChance: chance,
            unipageChance: unipageChance,
            countryPriority: countryPriority,
            potentialLangLevel: potentialLangLevel,
            isLangMatch: isLangMatch,
            isPreferred: isPreferred,
            visaRisk: visaRisk,
            workingResidencyLabel: workingResidencyLabel,
            visaRiskInfo: workingResidencyLabel ? getVisaRiskStatus(visaRisk) : null
          };
        });

        return results
          .sort((a, b) => {
            if (a.isLangMatch !== b.isLangMatch) return a.isLangMatch ? -1 : 1;

            const aLow = a.selfChance < 2;
            const bLow = b.selfChance < 2;

            // Если сортировка по умолчанию, сохраняем логику "низкие шансы в конце"
            if (sortBy.value === 'default') {
              if (aLow !== bLow) return aLow ? 1 : -1;
              return b.countryPriority - a.countryPriority;
            }

            // Для остальных режимов сортировки
            switch (sortBy.value) {
              case 'chances':
                if (b.unipageChance !== a.unipageChance) return b.unipageChance - a.unipageChance;
                break;
              case 'regions':
                if (a.isPreferred !== b.isPreferred) return a.isPreferred ? -1 : 1;
                break;
              case 'recommended':
                if ((b.recommended || 1) !== (a.recommended || 1)) {
                  return (b.recommended || 1) - (a.recommended || 1);
                }
                break;
              case 'cheapest':
                if (a.costTotal !== b.costTotal) return a.costTotal - b.costTotal;
                break;
              case 'quality':
                const aQual = a.quality || 999;
                const bQual = b.quality || 999;
                if (aQual !== bQual) return aQual - bQual;
                break;
              case 'popularity':
                if ((b.popularity || 0) !== (a.popularity || 0)) {
                  return (b.popularity || 0) - (a.popularity || 0);
                }
                break;
            }

            // Внутри групп или при равенстве критериев,
            // все же лучше ставить варианты с нормальными шансами выше
            if (aLow !== bLow) return aLow ? 1 : -1;
            return b.countryPriority - a.countryPriority;
          });
      }

      const paginatedResults = computed(() => processedResults.value.slice(0, displayedCount.value));

      const processedResults = computed(() => {
        let results = filteredResults.value;
        if (showOnlyFavorites.value) {
          results = results.filter(res => isFavorite(res));
        }
        if (!isDemoMode.value) return results;
        return results.map(res => {
          const countryWords = (res.country || "").split(" ").map(w => w.length);
          const regionWords = (res.region || "").split(" ").map(w => w.length);
          return {
            ...res,
            _countryWords: countryWords,
            _regionWords: regionWords,
            country: "",
            region: ""
          };
        });
      });

      function loadMore() {
        if (displayedCount.value < processedResults.value.length) {
          displayedCount.value += 10;
        }
      }

      async function submitRequestForm(e: SubmitEvent) {
        const phoneDigits = leadForm.phone.replace(/\D/g, '');
        if (leadForm.name.trim().length >= 2 && phoneDigits.length > 0) {
          try {
            const form = e.target;

            clearFormInputsErrors(form);

            const formData = new FormData(form);
            const response = await Request.prepare(formData, undefined, true);

            if (response.success) {
              localStorage.setItem('unipage_auth', 'true');
              isDemoMode.value = false;

              // Отправка результатов в консоль при разблокировке
              await prepareAndSaveResults();
            } else {
              showFormInputsErrors(form, response.errors);
            }
          } catch (e) {
          }
        } else {
          alert('Пожалуйста, введите имя и телефон.');
        }
      }

      function closeDemoModal() { isDemoMode.value = false; }

      function getStudyDurationLabel(val) {
        if (!val) return 'лет';

        let num = val;
        if (typeof val === 'string') {
          if (val.includes(',')) return 'года'; // Для дробных 1,5 года, 2,5 года
          const matches = val.match(/(\d+)(?!.*\d)/); // Берем последнее число из строки (для диапазонов)
          if (matches) {
            num = parseInt(matches[1], 10);
          } else {
            return 'лет';
          }
        }

        const n = Math.abs(num) % 100;
        const n1 = n % 10;
        if (n > 10 && n < 20) return 'лет';
        if (n1 > 1 && n1 < 5) return 'года';
        if (n1 === 1) return 'год';
        return 'лет';
      }

      function getFavoriteId(res) { return `${res.iso || ''}_${res.langCode || ''}_${res.uniType || ''}`; }
      function isFavorite(res) { return (answers.favorites || []).includes(getFavoriteId(res)); }
      async function toggleFavorite(res) {
        const id = getFavoriteId(res);
        const idx = (answers.favorites || []).indexOf(id);
        if (idx > -1) {
          answers.favorites.splice(idx, 1);
        } else {
          if (!answers.favorites) {
            answers.favorites = [];
          }
          answers.favorites.push(id);
        }
        await prepareAndSaveResults();
      }


      // Вспомогательные функции через UniPage.utils
      const getLangName = window.UniPage.utils.getLangName;
      const getLevelLabel = window.UniPage.utils.getLevelLabel;
      const formatMoney = window.UniPage.utils.formatMoney;
      const formatMoneyRange = (v) => window.UniPage.utils.formatMoneyRange(v);
      const formatGpaRange = (v) => window.UniPage.utils.formatGpaRange(v);
      const getVisaRiskStatus = window.UniPage.utils.getVisaRiskStatus;
      const formatValue = window.UniPage.utils.formatValue;
      const formatRange = (min, max) => window.UniPage.utils.formatRange(min, max);
      const formatDeadlines = window.UniPage.utils.formatDeadlines;

      function getStudyDuration(res) {
        const data = res?.study_duration;
        return data ? (formatRange(data.years_min, data.years_max) || formatValue(data.years_mid)) : null;
      }

      function getSchoolGrade(res) {
        const data = res?.school_grade;
        return data ? (formatRange(data.grade_min, data.grade_max) || formatValue(data.grade_mid)) : null;
      }

      function getEnrollmentAge(res) {
        const data = res?.enrollment_age;
        return data ? (formatRange(data.age_min, data.age_max) || formatValue(data.age_mid)) : null;
      }

      function hasAcademicConditions(res) {
        return !!(getStudyDuration(res) || getSchoolGrade(res) || getEnrollmentAge(res) || formatDeadlines(res.deadlines));
      }

      function formatWorkHours(res) {
        const hours = res?.visa?.study_work_conditions?.work_hours_per_week;
        return hours != null ? `До ${formatValue(hours)} час/нед` : null;
      }

      function formatPostStudy(postStudy) {
        if (!postStudy || postStudy.duration_min === 0) return postStudy?.duration_min === 0 ? 'Нет' : null;
        const range = formatRange(postStudy.duration_min, postStudy.duration_max);
        return range ? `${range} года` : null;
      }

      function formatFinancialThreshold(val) {
        const formatted = val != null ? formatMoney(Math.round(val)) : null;
        return formatted ? `$${formatted}+ в год` : null;
      }

      function formatSalary(val) {
        const formatted = val != null ? formatMoney(Math.round(val)) : null;
        return formatted ? `$${formatted} в мес` : null;
      }

      function buildTooltip(hint, comment) { return comment ? `${hint} ● ${comment}` : hint; }
      function buildPostStudyHint(postStudy) {
        const base = postStudy?.title ? `После завершения программы государство даёт возможность найти работу ● Виза: ${postStudy.title}.` : 'После завершения программы государство даёт возможность найти работу';
        return buildTooltip(base, postStudy?.comment);
      }


      function getChanceColor(percent) {
        let hue = percent <= 33 ? (percent / 33) * 40 : (percent <= 66 ? 40 + ((percent - 33) / 33) * 20 : 60 + ((percent - 66) / 34) * 60);
        return `hsl(${hue}, 80%, 40%)`;
      }

      function getRequirementColor(current, required) {
        if (!required) return '#198754';
        const percent = (current / required) * 100;
        let hue = percent < 80 ? (percent / 80) * 30 : (percent < 100 ? 30 + ((percent - 80) / 20) * 30 : (percent < 120 ? 60 + ((percent - 100) / 20) * 80 : 140));
        return `hsl(${hue}, 80%, 40%)`;
      }

      const suitableResultsCount = computed(() => filteredResults.value.filter(res => res.isLangMatch && res.selfChance > 0).length);
      const totalDatabaseCount = computed(() => DB_DATA.length);

      function getSourceFromQuery(): UnipageNavigatorAssessmentSource {
        const params = new URLSearchParams(window.location.search);
        const sourceParam = params.get("source");

        if (sourceParam && Object.values(UnipageNavigatorAssessmentSource).includes(sourceParam as UnipageNavigatorAssessmentSource)) {
          return sourceParam as UnipageNavigatorAssessmentSource;
        }

        return UnipageNavigatorAssessmentSource.APP;
      }

      watch(answers, (newVal) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newVal));
      }, { deep: true });

      watch(paginatedResults, () => {});
      watch(showOnlyFavorites, (newVal) => {
        localStorage.setItem('unipage_show_only_favorites', JSON.stringify(newVal));
        displayedCount.value = 10;
        scrollToFirstResult();
      });
      watch(sortBy, (newVal) => {
        displayedCount.value = 10;
        if (answers.sortBy !== newVal) {
          answers.sortBy = newVal;
          void prepareAndSaveResults();
        }
        scrollToFirstResult();
      });

      return {
        answers,
        resultsShown,
        filteredResults,
        processedResults,
        paginatedResults,
        displayedCount,
        loadMore,
        isDemoMode,
        leadForm,
        submitRequestForm,
        closeDemoModal,
        suitableResultsCount,
        totalDatabaseCount,
        consent,
        getLangName,
        getLevelLabel,
        formatMoney,
        formatMoneyRange,
        formatGpaRange,
        getChanceColor,
        getRequirementColor,
        toggleFavorite,
        isFavorite,
        formatDeadlines,
        getStudyDuration,
        getStudyDurationLabel,
        getSchoolGrade,
        getEnrollmentAge,
        hasAcademicConditions,
        formatWorkHours,
        formatPostStudy,
        formatFinancialThreshold,
        formatSalary,
        buildTooltip,
        buildPostStudyHint,
        showOnlyFavorites,
        showSortPanel,
        isAssessmentModalOpen,
        openAssessmentModal,
        closeAssessmentModal,
        onAssessmentComplete,
        sortBy,
        sortOptions,
        expandedIds,
        toggleExpand,
        isExpanded,
        getFavoriteId,
        expandedEducationIds,
        toggleEducation,
        isEducationExpanded,
        expandedUniversityIds,
        toggleUniversity,
        isUniversityExpanded,
        getCsrfToken,
        LoaderSize,
        UnipageNavigatorAssessmentSource,
        APP_CONFIG,
        showBudgetWarning,
        isCriticalBudget,
        handleScholarshipsClick,
        handleCancelBudget,
        assessmentInitialQuestionId
      };
    },
    template: `
      <div id="app">
        <div class="container-fluid p-0 main-wrapper">

          <!-- Results Screen -->
          <div class="results-screen">
            <div class="results-header d-flex justify-content-between align-items-end mb-5 flex-wrap gap-3">
              <div>
                <h2 class="h1 fw-bold mb-2">Подходящие страны</h2>
                <p class="text-secondary h5 fw-normal">На основе ваших ответов вам подходят <span
                    class="badge-primary-color">{{ suitableResultsCount }}</span> из <span class="badge-primary-color">{{ totalDatabaseCount }}</span>
                  вариантов</p>
              </div>
              <div class="d-flex gap-3 align-items-end flex-wrap">
                <div class="sort-control">
                  <label for="sort-select" class="small text-secondary mb-2 d-block">Сначала показывать</label>
                  <div class="position-relative">
                                <span class="sort-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                         stroke-linejoin="round">
                                        <polyline points="7 10 12 5 17 10"></polyline>
                                        <polyline points="7 14 12 19 17 14"></polyline>
                                    </svg>
                                </span>
                    <select id="sort-select" v-model="sortBy" class="sort-select">
                      <option v-for="{ value, label } in sortOptions" :key="value" :value="value">{{ label }}</option>
                    </select>
                  </div>
                </div>
                <button @click="openAssessmentModal"
                        class="button button_primary button_size_dynamic d-inline-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="me-2">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                  </svg>
                  Персональный фильтр
                </button>
              </div>
            </div>

            <div v-if="filteredResults.length === 0" class="no-results p-5 text-center rounded-4">
              <div class="h3 mb-3">Вариантов не найдено</div>
              <p class="text-secondary">Попробуйте изменить критерии поиска, например, уровень языка или бюджет.</p>
            </div>

            <div v-else class="results-list-container position-relative">
              <!-- Budget Warning Inline -->
              <div v-if="showBudgetWarning" class="budget-warning-inline p-4 p-md-5 mb-4 rounded-4 shadow-sm border bg-white overflow-hidden position-relative">
                <div class="row align-items-center g-4">
                  <div class="col-auto d-none d-md-block">
                    <div class="warning-icon-circle">
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f44336" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                  </div>
                  <div class="col text-center text-md-start">
                    <div class="d-md-none mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f44336" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <h3 class="fw-bold mb-2">Внимание: Бюджет ограничен</h3>
                    <p class="text-secondary mb-0" style="font-size: 1.1rem; line-height: 1.6;">
                      Обучение за границей в этом ценовом диапазоне практически невозможно без внешней помощи.
                      Мы рекомендуем ознакомиться с грантами или пересмотреть бюджет
                    </p>
                  </div>
                  <div class="col-12 col-xl-auto">
                    <div class="d-grid d-md-flex gap-3 justify-content-xl-end">
                      <button @click="handleScholarshipsClick" class="button button_primary py-3 px-4 rounded fw-bold">Стипендии и гранты</button>
                      <button @click="handleCancelBudget" class="button button_secondary py-3 px-4 rounded fw-bold">Изменить бюджет</button>
                    </div>
                  </div>
                </div>
                <div class="accent-line"></div>
              </div>

              <div class="results-list" :class="{ 'pb-lg-5': isDemoMode }">
                <div v-for="res in paginatedResults" :key="getFavoriteId(res)"
                     class="country-card card border-0 mb-4 overflow-hidden shadow">
                  <div class="card-body p-4 p-md-5">
                    <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                      <div>
                        <h3 class="country-name h1 fw-bold mb-1 d-flex align-items-baseline flex-wrap">
                                        <span class="country-flag flag me-3 align-self-center"
                                              :class="(isDemoMode || res.iso === 'other') ? 'flag-other' : 'flag-' + res.iso.toLowerCase()"></span>
                          <template v-if="!isDemoMode">
                            {{ res.country }}
                            <span class="region text-secondary fw-normal fs-5 ms-3">{{ res.region }}</span>
                          </template>
                          <template v-else>
                                            <span v-for="(wordLen, i) in res._countryWords" :key="'c'+i"
                                                  class="skeleton-box"
                                                  :class="i > 0 ? 'ms-2' : ''"
                                                  :style="{ width: (wordLen * 18 + 10) + 'px' }"></span>
                            <span v-for="(wordLen, i) in res._regionWords" :key="'r'+i"
                                  class="skeleton-box opacity-50 align-self-center"
                                  :class="i === 0 ? 'ms-3' : 'ms-2'"
                                  :style="{ width: (wordLen * 12 + 8) + 'px', height: '1.6rem' }"></span>
                          </template>
                        </h3>
                        <div class="badges-row d-flex flex-wrap gap-2 mt-3">
                                        <span class="badge-custom">
                                            <svg viewBox="0 0 24 24"><path
                                                d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/></svg>
                                          {{ getLangName(res.langCode) }} язык
                                        </span>
                          <span class="badge-custom">
                                            <svg viewBox="0 0 24 24"><path
                                                d="M11.5,1L2,6V8H21V6M16,10V17H19V10M12,10V17H15V10M8,10V17H11V10M4,10V17H7V10M2,19V21H21V19H2Z"/></svg>
                            {{ res.uniType === 'Public' ? 'Госвузы' : 'Частные вузы' }}
                                        </span>
                          <span class="badge-custom">
                                            <svg viewBox="0 0 24 24"><path
                                                d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/></svg>
                                            Бакалавриат
                                        </span>
                          <template v-if="!isDemoMode">
                                            <span v-if="(res.recommended || 1) > 1 && res.unipageChance >= 50"
                                                  class="badge-custom badge-success-light" v-tooltip
                                                  data-bs-title="Эксперты UniPage рекомендуют эту страну для поступления">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                     class="feather feather-thumbs-up"><path
                                                    d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>
                                                Рекомендуем
                                            </span>
                            <span v-if="res.isPreferred" class="badge-custom badge-success-light" v-tooltip
                                  :data-bs-title="'Это выбранный вами регион ' + res.region">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                     class="feather feather-map-pin"><path
                                                    d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><polyline
                                                    points="9 11 12 14 16 10"></polyline></svg>
                                                Выбранный регион
                                            </span>
                          </template>
                        </div>
                      </div>
                      <div class="chances-display d-flex gap-4 align-items-center">
                        <button v-if="res.unipageChance < 20 && !isExpanded(res) && !isFavorite(res)"
                                @click="toggleExpand(res)"
                                class="button button_secondary button_size_dynamic button_size_m d-inline-flex align-items-center">
                          Почему?
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                               class="feather feather-chevron-down ms-2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                        <div class="chance-item">
                          <div class="chance-val h2 fw-bold mb-0" :style="{ color: getChanceColor(res.selfChance) }">
                            {{ res.selfChance }}%
                          </div>
                          <div class="chance-label text-secondary small">Самостоятельно</div>
                        </div>
                        <div v-if="res.unipageChance >= 20 || isExpanded(res) || isFavorite(res)" class="chance-item">
                          <div class="chance-val h2 fw-bold mb-0" :style="{ color: getChanceColor(res.unipageChance) }">
                            {{ res.unipageChance }}%
                          </div>
                          <div class="chance-label text-secondary small">Шанс с UniPage</div>
                        </div>
                      </div>
                    </div>

                    <template v-if="res.unipageChance >= 20 || isExpanded(res) || isFavorite(res)">
                      <div class="row gy-5">
                        <!-- Cost Scale -->
                        <div class="col-12 col-lg-4">
                          <div class="scale-group">
                            <h4 class="h6 fw-bold mb-2 text-uppercase tracking-wider"
                                :style="{ color: getRequirementColor(answers.budget, res.costTotal) }">СТОИМОСТЬ В
                              ГОД</h4>
                            <div class="scale-visual mb-4">
                              <div class="scale-pin"
                                   :style="{ left: Math.min(100, (answers.budget / 55000 * 100)) + '%' }">
                                <div class="pin-marker"
                                     :style="{ borderTopColor: getRequirementColor(answers.budget, res.costTotal) }"></div>
                              </div>
                              <div class="scale-track">
                                <div class="segment segment-tuition"
                                     :style="{ width: (res.costTuition / 55000 * 100) + '%' }" title="Обучение"></div>
                                <div class="segment segment-apartments"
                                     :style="{ width: (res.costApartments / 55000 * 100) + '%' }" title="Жилье"></div>
                                <div class="segment segment-life" :style="{ width: (res.costLife / 55000 * 100) + '%' }"
                                     title="Жизнь"></div>
                              </div>
                            </div>
                            <div class="scale-legend">
                              <div class="legend-row">
                              <span class="legend-dot dot-user"
                                    :style="{ backgroundColor: getRequirementColor(answers.budget, res.costTotal) }"></span>
                                <span class="legend-label">Ваш бюджет</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatMoney(answers.budget) }} $</span>
                              </div>
                              <div class="legend-row">
                                <span class="legend-dot dot-cost-total"></span> <span
                                  class="legend-label">Общие расходы</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatMoneyRange(res.costTotal) }}
                                  $</span>
                              </div>
                              <div class="legend-row indented">
                                <span class="legend-dot dot-tuition"></span> <span class="legend-label">Обучение</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatMoneyRange(res.costTuition) }}
                                  $</span>
                              </div>
                              <div class="legend-row indented">
                                <span class="legend-dot dot-apartments"></span> <span
                                  class="legend-label">Аренда жилья</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatMoneyRange(res.costApartments) }}
                                  $</span>
                              </div>
                              <div class="legend-row indented">
                                <span class="legend-dot dot-life"></span> <span class="legend-label">Проживание</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatMoneyRange(res.costLife) }} $</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Lang Scale -->
                        <div class="col-12 col-lg-4">
                          <div class="scale-group">
                            <h4 class="h6 fw-bold mb-2 text-uppercase tracking-wider"
                                :style="{ color: getRequirementColor(answers.langLevels[res.langCode], res.langScore) }">
                              ЗНАНИЕ ЯЗЫКА</h4>
                            <div class="scale-visual mb-4">
                              <div class="scale-pin"
                                   :style="{ left: (answers.langLevels[res.langCode] / 6.99 * 100) + '%' }">
                                <div class="pin-marker"
                                     :style="{ borderTopColor: getRequirementColor(answers.langLevels[res.langCode], res.langScore) }"></div>
                              </div>
                              <!-- Potential Pin -->
                              <div
                                  v-if="res.potentialLangLevel > answers.langLevels[res.langCode] && answers.langLevels[res.langCode] < 6"
                                  class="scale-pin pin-potential"
                                  :style="{ left: (res.potentialLangLevel / 6.99 * 100) + '%' }">
                                <div class="pin-marker-potential"></div>
                              </div>
                              <div class="scale-track bg-dark-soft">
                                <div class="segment segment-required"
                                     :style="{ width: (res.langScore / 6.99 * 100) + '%' }"></div>
                              </div>
                            </div>
                            <div class="scale-legend">
                              <div class="legend-row">
                              <span class="legend-dot dot-user"
                                    :style="{ backgroundColor: getRequirementColor(answers.langLevels[res.langCode], res.langScore) }"></span>
                                <span class="legend-label">У вас сейчас</span>
                                <span
                                    class="legend-value fw-bold ms-auto">{{ getLevelLabel(answers.langLevels[res.langCode]) }}</span>
                              </div>
                              <!-- Potential Growth -->
                              <div
                                  v-if="res.potentialLangLevel > answers.langLevels[res.langCode] && answers.langLevels[res.langCode] < 6"
                                  class="legend-row">
                                <span class="legend-dot dot-potential"></span> <span
                                  class="legend-label">Можно изучить до</span>
                                <span class="legend-value fw-bold ms-auto">
                                                    {{ getLevelLabel(res.potentialLangLevel) }}
                                  {{ getLevelLabel(res.potentialLangLevel) === getLevelLabel(answers.langLevels[res.langCode]) ? '+' : '' }}
                                                </span>
                              </div>
                              <div class="legend-row">
                                <span class="legend-dot dot-required"></span> <span
                                  class="legend-label">Требуется вузами</span>
                                <span class="legend-value fw-bold ms-auto">{{ getLevelLabel(res.langScore) }}</span>
                              </div>
                              <div class="mt-3 small text-secondary fst-italic">{{ res.langScoreComment }}</div>
                            </div>
                          </div>
                        </div>

                        <!-- GPA Scale -->
                        <div class="col-12 col-lg-4">
                          <div class="scale-group">
                            <h4 class="h6 fw-bold mb-2 text-uppercase tracking-wider"
                                :style="{ color: getRequirementColor(answers.gpa, res.gpaLevel) }">СРЕДНИЙ БАЛЛ</h4>
                            <div class="scale-visual mb-4">
                              <div class="scale-pin" :style="{ left: ((answers.gpa - 1) / 3 * 100) + '%' }">
                                <div class="pin-marker"
                                     :style="{ borderTopColor: getRequirementColor(answers.gpa, res.gpaLevel) }"></div>
                              </div>
                              <div class="scale-track bg-dark-soft">
                                <div class="segment segment-required"
                                     :style="{ width: ((res.gpaLevel - 1) / 3 * 100) + '%' }"></div>
                              </div>
                            </div>
                            <div class="scale-legend">
                              <div class="legend-row">
                              <span class="legend-dot dot-user"
                                    :style="{ backgroundColor: getRequirementColor(answers.gpa, res.gpaLevel) }"></span>
                                <span class="legend-label">У вас сейчас</span>
                                <span class="legend-value fw-bold ms-auto">{{ answers.gpa }}</span>
                              </div>
                              <div class="legend-row">
                                <span class="legend-dot dot-required"></span> <span
                                  class="legend-label">Требуется вузами</span>
                                <span class="legend-value fw-bold ms-auto">{{ formatGpaRange(res.gpaLevel) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>


                      <template v-if="hasAcademicConditions(res)">
                        <div class="mt-4 mb-0">
                          <h4 class="h6 fw-bold text-uppercase tracking-wider text-secondary">АКАДЕМИЧЕСКИЕ УСЛОВИЯ</h4>
                        </div>
                        <div class="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 stats-grid mt-0">
                          <div v-if="getStudyDuration(res) !== null" class="col">
                            <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                                 :data-bs-title="buildTooltip('Продолжительность обучения по данной программе', res.study_duration?.comment)">
                              <div class="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                     stroke-linejoin="round" class="feather feather-info">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="12" y1="16" x2="12" y2="12"></line>
                                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                              </div>
                              <div class="stat-value">{{ getStudyDuration(res) }}
                                {{ getStudyDurationLabel(getStudyDuration(res)) }}
                              </div>
                              <div class="stat-subtext">Срок обучения</div>
                            </div>
                          </div>
                          <div v-if="getSchoolGrade(res) !== null" class="col">
                            <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                                 :data-bs-title="buildTooltip(\`Для поступления на данную программу требуется обязательное завершение \` + getSchoolGrade(res) + \` полных классов\`, res.school_grade?.comment)">
                              <div class="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                     stroke-linejoin="round" class="feather feather-info">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="12" y1="16" x2="12" y2="12"></line>
                                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                              </div>
                              <div class="stat-value">{{ getSchoolGrade(res) }} классов</div>
                              <div class="stat-subtext">Мин. образование</div>
                            </div>
                          </div>
                          <div v-if="getEnrollmentAge(res) !== null" class="col">
                            <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                                 :data-bs-title="buildTooltip(\`На момент поступления абитуриент должен быть старше \` + getEnrollmentAge(res) + \` лет\`, res.enrollment_age?.comment)">
                              <div class="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                     stroke-linejoin="round" class="feather feather-info">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="12" y1="16" x2="12" y2="12"></line>
                                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                              </div>
                              <div class="stat-value">{{ getEnrollmentAge(res) }} лет</div>
                              <div class="stat-subtext">Мин. возраст</div>
                            </div>
                          </div>
                          <div v-if="formatDeadlines(res.deadlines)" class="col">
                            <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                                 :data-bs-title="buildTooltip('Крайний срок подачи документов')">
                              <div class="stat-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                     stroke-linejoin="round" class="feather feather-info">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="12" y1="16" x2="12" y2="12"></line>
                                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                              </div>
                              <div class="stat-value">{{ formatDeadlines(res.deadlines) }}</div>
                              <div class="stat-subtext">Дедлайн</div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <div class="mt-5 mb-0">
                        <h4 class="h6 fw-bold text-uppercase tracking-wider text-secondary">ВИЗОВЫЕ ТРЕБОВАНИЯ И
                          ЛЕГАЛИЗАЦИЯ ДЛЯ РЕЗИДЕНТСТВА <span
                              class="badge-primary-color">{{ res.workingResidencyLabel }}</span></h4>
                      </div>
                      <div class="row row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 stats-grid mt-0">
                        <div v-if="res.visaRiskInfo" class="col">
                          <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                               :data-bs-title="buildTooltip(\`Вероятность отказа в визе \` + res.visaRisk + \`% для «\` + res.workingResidencyLabel + \`» в выбранную страну\`)">
                            <div class="stat-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="feather feather-info">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            </div>
                            <div class="stat-value" :style="{ color: res.visaRiskInfo.color }">
                              {{ res.visaRiskInfo.text }}
                            </div>
                            <div class="stat-subtext">Визовые риски</div>
                          </div>
                        </div>
                        <div v-if="formatFinancialThreshold(res.visa?.financial_threshold)" class="col">
                          <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                               :data-bs-title="buildTooltip('При подаче на визу необходимо предоставить документальное подтверждение источника дохода')">
                            <div class="stat-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="feather feather-info">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            </div>
                            <div class="stat-value">{{ formatFinancialThreshold(res.visa?.financial_threshold) }}</div>
                            <div class="stat-subtext">Справка о доходах для визы</div>
                          </div>
                        </div>

                        <div v-if="formatPostStudy(res.visa?.post_study_visa)" class="col">
                          <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                               :data-bs-title="buildPostStudyHint(res.visa?.post_study_visa)">
                            <div class="stat-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="feather feather-info">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            </div>
                            <div class="stat-value">{{ formatPostStudy(res.visa?.post_study_visa) }}</div>
                            <div class="stat-subtext">Право работы после учебы</div>
                          </div>
                        </div>
                        <div v-if="formatWorkHours(res) !== null" class="col">
                          <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                               :data-bs-title="buildTooltip('Возможность совмещать работу и учебу ограничена количеством часов и другими условиями визы', res.visa?.study_work_conditions?.comment)">
                            <div class="stat-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="feather feather-info">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            </div>
                            <div class="stat-value">{{ formatWorkHours(res) }}</div>
                            <div class="stat-subtext">Работа во время учебы</div>
                          </div>
                        </div>
                        <div v-if="formatSalary(res.salary?.graduate_mid)" class="col">
                          <div class="stat-card h-100" v-tooltip="true" data-bs-placement="top"
                               :data-bs-title="buildTooltip('Размер заработной платы после налогов для выпускников бакалавриата', res.salary?.comment)">
                            <div class="stat-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="feather feather-info">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                            </div>
                            <div class="stat-value">{{ formatSalary(res.salary?.graduate_mid) }}</div>
                            <div class="stat-subtext">Зарплата у выпускников</div>
                          </div>
                        </div>
                      </div>

                      <template v-if="res.education">
                        <div class="row g-3 mt-4 assessment-container">
                          <div
                              :class="isEducationExpanded(res) || isUniversityExpanded(res) ? 'col-12' : 'col-lg-6 col-6'">
                            <div class="accordion education-accordion">
                              <div class="accordion-item border-0">
                                <h2 class="accordion-header">
                                  <button class="accordion-button fw-bold text-uppercase tracking-wider"
                                          :class="{ collapsed: !isEducationExpanded(res) }"
                                          type="button"
                                          @click="toggleEducation(res)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                         fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                         stroke-linejoin="round" class="feather feather-file-text me-2 text-primary"
                                         style="position: relative; top: -1px;">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                      <polyline points="14 2 14 8 20 8"></polyline>
                                      <line x1="16" y1="13" x2="8" y2="13"></line>
                                      <line x1="16" y1="17" x2="8" y2="17"></line>
                                      <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    ВАЖНОЕ О СТРАНЕ
                                  </button>
                                </h2>
                                <div class="accordion-collapse collapse" :class="{ show: isEducationExpanded(res) }">
                                  <div class="accordion-body bg-transparent">
                                    <div v-if="res.education.overview" class="mb-4">
                                      <h5 class="h6 fw-bold mb-2">Бакалавриат для иностранцев</h5>
                                      <div style="white-space: pre-wrap; line-height: 1.6;">{{ res.education.overview }}
                                      </div>
                                    </div>
                                    <div v-if="res.education.pros && res.education.pros.length" class="mb-4">
                                      <h5 class="h6 fw-bold mb-2">Преимущества</h5>
                                      <ul class="list-unstyled mb-0">
                                        <li v-for="proText in res.education.pros" :key="proText"
                                            class="d-flex align-items-start mb-2">
                                                                    <span class="text-success me-2 flex-shrink-0"
                                                                          style="margin-top: 2px;">
                                                                        <svg xmlns="http://www.w3.org/2000/svg"
                                                                             width="18" height="18" viewBox="0 0 24 24"
                                                                             fill="none" stroke="currentColor"
                                                                             stroke-width="2" stroke-linecap="round"
                                                                             stroke-linejoin="round"><line x1="12"
                                                                                                           y1="5"
                                                                                                           x2="12"
                                                                                                           y2="19"></line><line
                                                                            x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                                    </span>
                                          <span style="line-height: 1.5;">{{ proText }}</span>
                                        </li>
                                      </ul>
                                    </div>
                                    <div v-if="res.education.cons && res.education.cons.length" class="mb-4">
                                      <h5 class="h6 fw-bold mb-2">Недостатки</h5>
                                      <ul class="list-unstyled mb-0">
                                        <li v-for="conText in res.education.cons" :key="conText"
                                            class="d-flex align-items-start mb-2">
                                                                    <span class="text-danger me-2 flex-shrink-0"
                                                                          style="margin-top: 2px;">
                                                                        <svg xmlns="http://www.w3.org/2000/svg"
                                                                             width="18" height="18" viewBox="0 0 24 24"
                                                                             fill="none" stroke="currentColor"
                                                                             stroke-width="2" stroke-linecap="round"
                                                                             stroke-linejoin="round"><line x1="5"
                                                                                                           y1="12"
                                                                                                           x2="19"
                                                                                                           y2="12"></line></svg>
                                                                    </span>
                                          <span style="line-height: 1.5;">{{ conText }}</span>
                                        </li>
                                      </ul>
                                    </div>
                                    <div class="mb-4">
                                      <h5 class="h6 fw-bold ms-2">Честная оценка и стратегия под ваши данные</h5>
                                      <div style="white-space: pre-wrap; line-height: 1.6;">
                                        <p>
                                          Если средний балл или уровень языка пока не идеальны, это не повод
                                          отказываться
                                          от мечты.
                                          Мы разработаем грамотную стратегию: подберем вузы, где ваши шансы наиболее
                                          высоки, поможем правильно расставить акценты в резюме и оформить документы
                                          так,
                                          чтобы вы гарантировано поступили.
                                        </p>
                                        <a class="button button_primary button_size_dynamic button_size_s"
                                           :href="APP_CONFIG.REQUEST_CREATE_URL">Помощь эксперта</a>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                              :class="isEducationExpanded(res) || isUniversityExpanded(res) ? 'col-12' : 'col-lg-6 col-6'">
                            <div class="accordion university-accordion">
                              <div class="accordion-item border-0">
                                <h2 class="accordion-header">
                                  <button class="accordion-button fw-bold text-uppercase tracking-wider"
                                          :class="{ collapsed: !isUniversityExpanded(res) }"
                                          type="button"
                                          @click="toggleUniversity(res)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                         fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                         stroke-linejoin="round" class="me-2 text-primary"
                                         style="position: relative; top: -1px;">
                                      <line x1="3" y1="21" x2="21" y2="21"></line>
                                      <path d="M3 7l9-4 9 4v2H3V7z"></path>
                                      <path d="M5 21V9h2v12"></path>
                                      <path d="M9 21V9h2v12"></path>
                                      <path d="M13 21V9h2v12"></path>
                                      <path d="M17 21V9h2v12"></path>
                                    </svg>
                                    Университеты
                                    <span class="uni-badge ms-2"
                                          v-if="res.universities?.total">{{ res.universities.total }}</span>
                                  </button>
                                </h2>
                                <div class="accordion-collapse collapse" :class="{ show: isUniversityExpanded(res) }">
                                  <div class="accordion-body bg-transparent p-0 pt-3">
                                    <Loader :size="LoaderSize.L"
                                            :class="'js-universities-list-loader-' + res.id"></Loader>
                                    <div :class="'js-universities-list-container-' + res.id"></div>
                                    <div class="p-3 border-top mt-3" v-if="isUniversityExpanded(res)">
                                      <a :href="'https://www.unipage.net/ru/universities?country_ids[]=' + res.id"
                                         target="_blank"
                                         class="button button_size_dynamic button_size_s button_secondary w-100 justify-content-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                             viewBox="0 0 24 24"
                                             fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                             stroke-linejoin="round" class="me-2">
                                          <line x1="3" y1="21" x2="21" y2="21"></line>
                                          <path d="M3 7l9-4 9 4v2H3V7z"></path>
                                          <path d="M5 21V9h2v12"></path>
                                          <path d="M9 21V9h2v12"></path>
                                          <path d="M13 21V9h2v12"></path>
                                          <path d="M17 21V9h2v12"></path>
                                        </svg>
                                        <span>Полный список</span>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </template>
                  </div>
                  <template v-if="res.unipageChance >= 20 || isExpanded(res) || isFavorite(res)">
                    <div class="card-footer-custom d-flex gap-3 justify-content-end align-items-center">
                      <div class="d-flex gap-2 me-auto">
                        <a target="_blank" :href="APP_CONFIG.REQUEST_CREATE_URL"
                           class="button button_primary button_size_dynamic button_size_s">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                               class="feather feather-help-circle">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                          </svg>
                          <span>Консультация<span class="d-none d-md-inline">&nbsp;эксперта</span></span>
                        </a>
                      </div>
                      <div class="d-flex align-items-center gap-3">
                        <button class="btn-favorite-large" @click="toggleFavorite(res)"
                                :class="{ active: isFavorite(res) }"
                                :title="isFavorite(res) ? 'Убрать из избранного' : 'Добавить в избранное'">
                          <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 24 24"
                               :fill="isFavorite(res) ? 'var(--primary-color)' : 'none'"
                               :stroke="isFavorite(res) ? 'var(--primary-color)' : 'currentColor'" stroke-width="2"
                               stroke-linecap="round" stroke-linejoin="round">
                            <path
                                d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Infinite scroll trigger -->
                <div v-if="displayedCount < processedResults.length" v-intersection="loadMore" class="text-center p-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Загрузка...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lead Wall Modal -->
          <div v-show="resultsShown && isDemoMode && !isAssessmentModalOpen" class="lead-wall-modal-overlay">
            <div class="modal-dialog">
              <div class="modal-content shadow-lg border-0 position-relative">
                <div class="modal-body py-5 px-4 px-md-5">
                  <div class="row align-items-start">
                    <div class="col-xl-4 col-lg-12 mb-2 text-center text-xl-start">
                      <h3 class="fw-bold mb-2 h4">Получить полный доступ <u>БЕСПЛАТНО</u></h3>
                      <p class="text-secondary mb-0"><span class="badge-primary-color">{{ suitableResultsCount }}</span>
                        из <span class="badge-primary-color">{{ totalDatabaseCount }}</span> результатов</p>
                    </div>
                    <div class="col-xl-8 col-lg-12">
                      <form id="navigator-request-form" @submit.prevent="submitRequestForm"
                            class="row g-2 align-items-start">
                        <div class="col-6 col-lg-4 form-block form-block_size_s required">
                          <label for="firstname" class="form-label">Ваше имя</label>
                          <input id="firstname" name="firstname" type="text" v-model="leadForm.name" class="form-input"
                                 placeholder="Иван" required minlength="2">
                        </div>
                        <div class="col-6 col-lg-4 form-block mt-0 form-block_size_s required">
                          <label for="phone" class="form-label">Телефон</label>
                          <input id="phone" name="phone" type="tel" v-model="leadForm.phone" class="form-input"
                                 placeholder="+ 7 (499) 677-18-64" required>
                        </div>
                        
                        <input type="hidden" name="_csrf" :value="getCsrfToken()">

                        <div class="col-12 col-lg-4">
                          <button type="submit" class="button button_primary button_inverted button_size_m w-100 mt-md-4">
                            Открыть результаты
                          </button>
                        </div>
                      </form>
                      <div ref="consent" class="mt-2 small text-secondary text-center text-xl-end" style="font-size: 1.4rem;">
                        Нажимая, я принимаю <a href="/ru/terms" target="_blank" class="text-inherit a_w">условия
                        UniPage</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Toggle Widgets -->
          <div class="toggle-widgets">
            <!-- Filter Widget -->
            <div class="toggle-widget-item" @click="openAssessmentModal"
                 v-tooltip="true" data-bs-placement="left" data-bs-title="Персональный фильтр">
              <div class="toggle-widget-badge badge-danger">
                {{ totalDatabaseCount }}
              </div>

              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
            </div>

            <!-- Sort Widget -->
            <div class="toggle-widget-item" :class="{ active: showSortPanel }" @click="showSortPanel = !showSortPanel"
                 v-tooltip="true" data-bs-placement="left" data-bs-title="Сначала показывать">
              <div class="toggle-widget-badge badge-success" v-if="suitableResultsCount > 0">
                {{ suitableResultsCount }}
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="7 10 12 5 17 10"></polyline>
                <polyline points="7 14 12 19 17 14"></polyline>
              </svg>
              <div v-if="showSortPanel" class="sort-options-panel" @click.stop>
                <div v-for="{ value, label } in sortOptions" :key="value"
                     class="sort-option-item" :class="{ active: sortBy === value }"
                     @click="sortBy = value; showSortPanel = false">
                  {{ label }}
                </div>
              </div>
            </div>


            <!-- Favorites Widget -->
            <div v-if="answers.favorites && answers.favorites.length > 0"
                 class="toggle-widget-item favorites-widget"
                 :class="{ active: showOnlyFavorites }"
                 @click="showOnlyFavorites = !showOnlyFavorites"
                 v-tooltip="true" data-bs-placement="left"
                 :data-bs-title="showOnlyFavorites ? 'Показать все варианты' : 'Отобразить только избранные варианты'">
              <div class="toggle-widget-badge badge-highlight">{{ answers.favorites.length }}</div>
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                   :fill="showOnlyFavorites ? 'currentColor' : 'none'"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path
                    d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </div>
          </div>

          <!-- Assessment Modal -->
          <div v-if="isAssessmentModalOpen" class="modal-backdrop fade show"></div>
          <div class="modal fade" :class="{ show: isAssessmentModalOpen }"
               :style="{ display: isAssessmentModalOpen ? 'block' : 'none' }" id="assessmentModal" tabindex="-1"
               aria-labelledby="assessmentModalLabel" :aria-hidden="!isAssessmentModalOpen">
            <div class="modal-dialog modal-xl modal-dialog-centered">
              <div class="modal-content border-0 shadow-lg" style="border-radius: 1.5rem; overflow: hidden;">
                <!-- Close button -->
                <button type="button" class="btn-close-modal" @click="closeAssessmentModal" aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <div class="modal-body p-0">
                  <AssessmentComponent v-if="isAssessmentModalOpen" 
                                       mode="popup" 
                                       :initial-question-id="assessmentInitialQuestionId"
                                       :source="UnipageNavigatorAssessmentSource.APP"
                                       @complete="onAssessmentComplete"></AssessmentComponent>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  });
  app.directive('tooltip', {
    mounted(el) {
      el._tooltipHandler = () => {
        if (el._tooltipEl) return;
        const title = el.getAttribute('data-bs-title') || el.getAttribute('title');
        if (!title) return;
        el._lastOpened = Date.now();
        const tooltip = document.createElement('div');
        tooltip.className = 'tooltip bs-tooltip-auto fade show';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.style.position = 'fixed';
        tooltip.style.zIndex = '1080';
        tooltip.style.pointerEvents = 'none'; // Чтобы не мешал кликам
        const arrow = document.createElement("div");
        arrow.className = "tooltip-arrow";
        arrow.style.position = "absolute";
        arrow.style.transform = "translate3d(0px,0px,0px)";
        const inner = document.createElement("div");
        inner.className = "tooltip-inner";
        inner.textContent = title;
        tooltip.append(arrow, inner);
        const container = document.querySelector('#navigator-app') || document.body;
        container.appendChild(tooltip);
        el._tooltipEl = tooltip;
        const rect = el.getBoundingClientRect();
        const placement = el.getAttribute('data-bs-placement') || 'top';
        let top, left;
        if (placement === 'top') {
          top = rect.top - tooltip.offsetHeight - 8;
          left = rect.left + (rect.width - tooltip.offsetWidth) / 2;
        } else if (placement === 'bottom') {
          top = rect.bottom + 8;
          left = rect.left + (rect.width - tooltip.offsetWidth) / 2;
        } else if (placement === 'left') {
          top = rect.top + (rect.height - tooltip.offsetHeight) / 2;
          left = rect.left - tooltip.offsetWidth - 8;
        } else if (placement === 'right') {
          top = rect.top + (rect.height - tooltip.offsetHeight) / 2;
          left = rect.right + 8;
        }
        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
      };
      el._tooltipLeaveHandler = () => {
        if (el._tooltipEl) { el._tooltipEl.remove(); el._tooltipEl = null; }
      };
      el._tooltipToggleHandler = (e) => {
        e.stopPropagation();
        if (el._tooltipEl) {
          // Предотвращаем закрытие, если тултип только что открылся (на мобилках mouseenter + click)
          if (Date.now() - (el._lastOpened || 0) > 400) {
            el._tooltipLeaveHandler();
          }
        } else {
          el._tooltipHandler();
        }
      };

      el.addEventListener('mouseenter', el._tooltipHandler);
      el.addEventListener('mouseleave', el._tooltipLeaveHandler);
      el.addEventListener('click', el._tooltipToggleHandler);

      el._docClickHandler = (e) => {
        if (el._tooltipEl && !el.contains(e.target)) {
          el._tooltipLeaveHandler();
        }
      };
      document.addEventListener('click', el._docClickHandler);
    },
    unmounted(el) {
      if (el._tooltipEl) el._tooltipEl.remove();
      el.removeEventListener('mouseenter', el._tooltipHandler);
      el.removeEventListener('mouseleave', el._tooltipLeaveHandler);
      el.removeEventListener('click', el._tooltipToggleHandler);
      document.removeEventListener('click', el._docClickHandler);
    }
  })
    .directive('intersection', {
      mounted(el, binding) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => { if (entry.isIntersecting) binding.value(); });
        }, { threshold: 0.1 });
        observer.observe(el);
        el._observer = observer;
      },
      unmounted(el) { if (el._observer) el._observer.disconnect(); }
    })
    .mount('#navigator-app');
}

initApp();
